import create from 'zustand';
import * as Yup from 'yup';
import {
  TiktokTargetGroup,
  YTTargetGroup,
} from 'pages/calculator/MediaPlanning/components/Touchpoints/interfaces';
import { Countries } from '../shared/enums';
import { Advertiser } from '../pages/calculator/MediaPlanning/Plan/Countries/NestedCountryTabs/Offline/interfaces';
import { Touchpoint } from '../api/services/mediaPlanning/touchpoints';
import { OOHBudget } from '../api/services/mediaPlanning/offline/oohBudget';
import { MetaFanpage } from '../api/services/mediaPlanning/types';

export interface GeneralInfoState {
  campaignName: string;
  campaignCode: string;
  division: string;
  brand: string;
  category: string;
}

export interface YouTubeConfig {
  isGoldenRuleMet?: boolean;
  bumperMin?: number;
  bumperMax?: number;
  bumperDefault?: number;
  unskipMin?: number;
  unskipMax?: number;
  unskipDefault?: number;
  saturationDefault?: number | null;
  recommendedTargetGroup?: string;
  bumperValue?: number | null;
  unSkipValue?: number | null;
  skipValue?: number | null;
  saturation?: number | null;
  targetGroup?: YTTargetGroup | null;
  targetGroups?: YTTargetGroup[];
  partners?: { id: string; title: string }[];
  partnerBudgetShare?: number | null;
  savedTargetGroup?: string | null;
  saturationMin?: number | null;
  saturationMax?: number | null;
  minReach?: number | null;
  grReach?: number | null;
  error?: boolean;
}

export interface MetaConfig {
  campaignDestination?: MetaFanpage;
  saturation?: number | null;
  targetGroup?: string | null;
  campaignLength?: string | null;
  startDate?: string | null;
  partners?: { id: string; title: string }[];
  partnerBudgetShareConversion?: number | null;
  partnerBudgetShareConsideration?: number | null;
  metaMinReachFactor?: number | null;
  metaConverts?: boolean;
  savedTargetGroup?: string | null;
  isGoldenRuleMet?: boolean;
  saturationMin?: number | null;
  targetGroups?: string[];
  saturationMax?: number | null;
  saturationDefault?: number | null;
  error?: boolean;
  reachEstimateMin?: number | null;
  metaCampaignId?: string | null;
  metaPredictionId?: string | null;
  recommendedTargetGroup?: string | null;
  isMaxSaturationExtended?: boolean;
  metaAccountId?: string;
  campaignDestinationIds?: string[];
  isMetaPartialPredictionEnabled?: boolean;
  minReach?: number | null;
  grReach?: number | null;
  metaPredictionStatuses?: {
    mainPredictionStatus?: {
      code: number;
      status: string;
      description: string;
    };
    partialPredictionStatus?: {
      code: number;
      status: string;
      description: string;
    };
  };
  reservationStatus?: string;
}

export interface TikTokConfig {
  isGoldenRuleMet?: boolean;
  targetGroup?: TiktokTargetGroup | null;
  targetGroups?: TiktokTargetGroup[];
  saturation?: number | null;
  partners?: { id: string; title: string }[];
  partnerBudgetShare?: number | null;
  savedTargetGroup?: string | null;
  saturationMin?: number | null;
  saturationMax?: number | null;
  saturationDefault?: number | null;
  recommendedTargetGroup?: string;
  minReach?: number | null;
  grReach?: number | null;
  error?: boolean;
}

export interface CalculatedBudget {
  touchpointName: string;
  touchpointFunnel: string;
  isMandatory: boolean;
  isIncludedInCalculations: boolean;
  collabPartnerName?: string;
  isBrandSideOfCollabSplit?: boolean;
  // TODO: add proper types
  oneReachCalculationVariants: any;
  isMetaPartialPredictionEnabled: boolean;
  budget: {
    value: number;
    currency: string;
  };
  impressions: number;
  reach: number;
  clicks: number;
  videoViews: number;
  segmentShare: number;
  techCost: {
    dsp: number;
    adServer: number;
  };
  recommendedTargetGroup?: string;
  metaCampaignId?: string;
  metaPredictionId?: string;
  metaPredictionStatuses: {
    mainPredictionStatus: {
      code: number;
      status: string;
      description: string;
    };
    partialPredictionStatus: {
      code: number;
      status: string;
      description: string;
    };
  };
  reachEstimateMin?: number;
}

export interface CopyValue {
  id: string;
  name: string;
  value: number;
}

export interface Copy {
  lengthPerWeek: number;
  value: CopyValue[];
}

export interface Week {
  weekNumber: number;
  startingDate: string;
  numberOfDays: number;
  firstDay: string;
  copy: Copy;
}

export interface GRPTable {
  planTotals: number[];
  grandTotal: number;
  columnTotals: number[];
  weeks: Week[];
}

export interface WeeklyWeight {
  min: number;
  max: number;
  current: number;
}

export interface CopyFormEntry {
  copyLength?: string;
  additionalAdvertisers?: string | null;
  advertisers?: Advertiser[];
  id?: string;
}

export interface Budget {
  value: number;
  currency: string;
}

export interface ResponseCopy {
  campaignBudget: Budget;
  name: string;
  id: string;
  split: number;
  totalGrp: number;
  weeks: number;
}

export interface CalculatedTVBudget {
  copies?: ResponseCopy[];
  reach1Plus?: number;
  reach3Plus?: number;
  techCost?: Budget;
  touchpointName?: string;
}

export interface OfflineBudgetCopy {
  id: string;
  name: string;
}

export interface OfflineBudgetChild {
  id: number;
  copy?: OfflineBudgetCopy;
  type?: string;
  option?: string;
  grp?: number;
  split?: number;
  weeks?: number;
  budget: Budget;
  isIncludedInCalculations?: boolean;
}

export interface OfflineBudgetEntry {
  id: number;
  touchpoint: string;
  budget: Budget;
  tvReachOne?: number;
  tvReachThree?: number;
  grp?: number;
  weeks?: number;
  children?: OfflineBudgetChild[];
}

export interface TVConfig {
  isAvailable?: boolean;
  weeklyWeight?: WeeklyWeight;
  startDate?: string;
  endDate?: string;
  minCampaignLength?: number;
  maxCampaignLength?: number;
  recommendedCampaignLength?: number;
  targetGroup?: string | null;
  targetGroups?: string[];
  recommendedTargetGroup?: string | null;
  grpTable?: GRPTable;
  copyForm: CopyFormEntry[];
  calculatedBudget?: CalculatedTVBudget;
  savedCalculatedBudget?: CalculatedTVBudget;
  offlineBudget?: OfflineBudgetEntry[];
  maxActWeeks?: number;
  maxWeeklyGrp?: number;
  minActWeeks?: number;
  minWeeklyGrp?: number;
  recoActWeeks?: number;
  recoWeeklyGrp?: number;
}

export interface OOHConfig {
  isAvailable: boolean;
  calculatedBudget?: OOHBudget;
  savedCalculatedBudget?: OOHBudget;
}

export type DdxState = {
  value?: number;
  type: 'fixed' | 'percentage' | 'omitted';
  totalDDX?: number;
} | null;

export interface CountryFormState {
  targetGroup: string;
  campaignScenario: string;
  segments: { id: string; name: string }[];
  startDate: string;
  campaignLength: string;
  isTVIncluded: boolean | null;
  isTVMandatory: boolean;
  isCountrySetupConfirmed: boolean;
  youTubeConfig?: YouTubeConfig;
  metaConfig?: MetaConfig;
  tiktokConfig?: TikTokConfig;
  calculatedBudget?: CalculatedBudget[];
  savedCalculatedBudget?: Partial<CalculatedBudget[]>;
  availableTouchpoints?: Touchpoint[];
  ddx?: DdxState;
  oneReach?: number;
  offline?: {
    savedTVConfig?: Partial<TVConfig>;
    tv?: TVConfig;
    ooh?: OOHConfig;
  };
}

export interface Segment {
  id: string;
  title: string;
}

export interface TechCost {
  dsp?: number;
  adServer?: number;
}

export interface BudgetComparision {
  value: number;
  currency: string;
}

export interface CalculatedBudgetEntry {
  touchpointName: string;
  touchpointFunnel: string;
  isMandatory?: boolean;
  isIncludedInCalculations?: boolean;
  budget?: BudgetComparision;
  impressions?: number;
  reach?: number;
  clicks?: number;
  videoViews?: number;
  techCost?: TechCost;
}

export interface OfflineBudgetComparisionEntry {
  touchpoint: string;
  budget?: BudgetComparision;
  children?: OfflineBudgetEntry[];
}

export interface CountrySetupEntry {
  targetGroup?: string;
  campaignScenario?: string;
  segments?: Segment[];
  startDate?: string;
  campaignLength?: number;
  oneReach?: number;
  calculatedBudget?: CalculatedBudgetEntry[];
  offline?: {
    tv?: {
      offlineBudget?: OfflineBudgetComparisionEntry[];
    };
  };
  availableTouchpoints?: {
    touchpointFunnel: string;
    TouchpointName: string;
    isMustHave: number;
  }[];
}

export interface ComparedPlan {
  id: string;
  planName?: string;
  campaignName?: string;
  campaignCode?: string;
  division?: string;
  brand?: string;
  category?: string;
  countrySetup?: Record<string, CountrySetupEntry>;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

export type DdxFixedTier = string | null;

export type DdxSetup = {
  isMustHave: number | null;
  isPercentage: number | null;
  tiers?: Array<
    | { fixedTier1: DdxFixedTier }
    | { fixedTier2: DdxFixedTier }
    | { fixedTier3: DdxFixedTier }
  >;
};

interface PlanStore {
  // General Info
  comparedPlans: Record<string, ComparedPlan>;
  setComparedPlan: (id: string, body?: Partial<ComparedPlan>) => void;
  setComparedPlans: (plans: ComparedPlan[]) => void;
  removeComparedPlan: (id: string) => void;
  resetComparedPlans: () => void;
  alreadySavedPlanId: string | null;
  metaBooked: boolean;
  activeTab: string;
  activeNestedTab: string;
  setActiveTab: (tab: string) => void;
  setActiveNestedTab: (tab: string) => void;
  isGeneralInfoFilled: boolean;
  setIsGeneralInfoFilled: (isFilled: boolean) => void;
  isPlanCreating: boolean;
  setIsPlanCreating: (isCreating: boolean) => void;
  isLocked: boolean;
  setIsLocked: (isLocked: boolean) => void;
  generalInfoState: GeneralInfoState;
  setGeneralInfoState: (state: Partial<GeneralInfoState>) => void;
  resetGeneralInfoState: () => void;
  setAlreadySavedPlanId: (id?: string) => void;
  setMetaBooked: (isBooked: boolean) => void;
  setCountryOneReach: (country: string, oneReach?: number) => void;
  saveTVConfig: (country: string) => void;

  // Saved General Info State
  savedGeneralInfoState: GeneralInfoState | null;
  saveGeneralInfoState: () => void;
  resetSavedGeneralInfoState: () => void;
  revertGeneralInfoState: () => void;

  // Country Management
  countryForms: Record<string, CountryFormState>;
  addCountry: (country: string) => void;
  updateCountryForm: (country: string, data: Partial<CountryFormState>) => void;
  updateYouTubeConfig: (
    country: string,
    config: Partial<YouTubeConfig>
  ) => void;
  updateMetaConfig: (country: string, config: Partial<MetaConfig>) => void;
  updateTikTokConfig: (country: string, config: Partial<TikTokConfig>) => void;
  updateCalculatedBudget: (country: string, budget: CalculatedBudget[]) => void;
  updateCalculatedTVBudget: (
    country: string,
    budget: CalculatedTVBudget
  ) => void;
  updateCalculatedOOHBudget: (country: string, budget: OOHBudget) => void;
  updateTVConfig: (
    country: string,
    tvConfig: Partial<TVConfig>,
    copyIndex?: number
  ) => void;
  updateCopyFormField: (
    country: string,
    copyIndex: number,
    field: keyof CopyFormEntry,
    value: any
  ) => void;

  addCopyFormEntry: (country: string, index: number) => void;

  removeCopyFormEntry: (country: string, copyIndex: number) => void;

  resetOfflineTables: (country: string) => void;
  resetCopyFormEntry: (country: string, copyIndex: number) => void;
  resetCalculatedBudget: (country: string) => void;
  updateSavedCalculatedBudget: (
    country: string,
    budget: Partial<CalculatedBudget[]>
  ) => void;
  resetSavedCalculatedBudget: (country: string) => void;
  resetTVConfig: (country: string) => void;
  resetOOHConfig: (country: string) => void;
  removeCountry: (country: string) => void;
  resetCountryForms: () => void;
  isCountryFormValid: (country: string) => boolean;
  updateOOHAvailable: (country: string, isAvailable: boolean) => void;
  // TV budget management

  updateTVAvailable: (country: string, isAvailable: boolean) => void;

  updateOfflineBudget: (country: string) => void;

  resetOfflineBudget: (country: string) => void;

  // Saved Country States
  updateMetaPredictionsState: (
    country: string,
    metaCampaignId?: string | null,
    metaPredictionId?: string | null
  ) => void;
  savedCountryForms: Record<string, Partial<CountryFormState>>;
  storedCountryForms: Record<string, Partial<CountryFormState>>;
  saveCountryFormState: (country: string) => void;
  storeCountryFormState: (country: string) => void;
  saveDigitalCountryFormState: (country: string) => void;
  storeDigitalCountryFormState: (country: string) => void;
  syncMetaState: (
    country: string,
    metaPredictionId: string,
    calculatedBudget: CalculatedBudget[]
  ) => void;
  saveOfflineCountryFormState: (country: string) => void;
  storeOfflineCountryFormState: (country: string) => void;
  revertCountryFormState: (country: string) => void;
  resetAllSavedCountryForms: () => void;
  resetAllStoredCountryForms: () => void;
  resetStoredCountryForm: (country: string) => void;
  resetSavedCountryForm: (country: string) => void;

  // Reset Configuration Only
  resetCountryFormConfig: (country: string) => void;

  // Validation Management
  validationErrors: Record<string, Record<string, string>>;
  validateCountryForm: (country: string, data: CountryFormState) => void;

  // Selected Country Panel
  selectedCountry: string | null;
  setSelectedCountry: (country: string | null) => void;
  selectedCountryISOCode: string | null;
  setSelectedCountryISOCode: (country: string | null) => void;

  // DDX
  ddxSetupByCountry: Record<string, Partial<DdxSetup> | undefined>;
  setDdxSetupByCountry: (
    setupMap: Record<string, Partial<DdxSetup> | undefined>
  ) => void;
  resetDdxSetupByCountry: () => void;
  setCountryDdx: (country: string, ddx: DdxState) => void;
}

const countryFormSchema = Yup.object().shape({
  startDate: Yup.string().nullable().required('Start Date is required'),
});

const usePlanStore = create<PlanStore>((set, get) => ({
  // General Info
  alreadySavedPlanId: null,
  metaBooked: false,
  setMetaBooked: (isBooked) => set({ metaBooked: isBooked }),
  activeTab: 'generalInfo',
  activeNestedTab: 'digital',
  setActiveTab: (tab) => set({ activeTab: tab }),
  setActiveNestedTab: (tab) => set({ activeNestedTab: tab }),
  isGeneralInfoFilled: false,
  setIsGeneralInfoFilled: (isFilled) => set({ isGeneralInfoFilled: isFilled }),
  isPlanCreating: false,
  setIsPlanCreating: (isCreating) => set({ isPlanCreating: isCreating }),
  isLocked: false,
  setIsLocked: (isLocked) => set({ isLocked }),
  generalInfoState: {
    campaignName: '',
    campaignCode: '',
    division: '',
    brand: '',
    category: '',
  },
  comparedPlans: {},
  setComparedPlan: (id: string, body: Partial<ComparedPlan> = {}) =>
    set((state) => {
      const prev = state.comparedPlans[id];

      return {
        comparedPlans: {
          ...state.comparedPlans,
          [id]: {
            // ensure id is always there
            ...(prev ?? { id }),
            // merge new partial data
            ...body,
          },
        },
      };
    }),
  setComparedPlans: (plans: ComparedPlan[]) =>
    set((state) => {
      const nextCompared = { ...state.comparedPlans };

      plans.forEach((plan) => {
        const { id } = plan;
        const prev = nextCompared[id];

        nextCompared[id] = {
          ...(prev ?? { id }),
          ...plan,
        };
      });

      return { comparedPlans: nextCompared };
    }),
  resetComparedPlans: () => set({ comparedPlans: {} }),
  removeComparedPlan: (id) =>
    set((state) => {
      const next = { ...state.comparedPlans };
      delete next[id];
      return { comparedPlans: next };
    }),

  ddxSetupByCountry: {},
  setDdxSetupByCountry: (setupMap) =>
    set(() => ({
      ddxSetupByCountry: setupMap,
    })),
  resetDdxSetupByCountry: () =>
    set(() => ({
      ddxSetupByCountry: {},
    })),

  setCountryDdx: (country: string, ddx: DdxState) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          ddx,
        },
      },
    })),

  updateMetaPredictionsState: (country, metaCampaignId, metaPredictionId) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          metaConfig: {
            ...state.countryForms[country].metaConfig,
            metaCampaignId,
            metaPredictionId,
          },
        },
      },

      ...(state.savedCountryForms?.[country] &&
        Object.keys(state.savedCountryForms[country]).length > 0 && {
          savedCountryForms: {
            ...state.savedCountryForms,
            [country]: {
              ...state.savedCountryForms[country],
              metaConfig: {
                ...state.savedCountryForms[country].metaConfig,
                metaCampaignId,
                metaPredictionId,
              },
            },
          },
        }),

      ...(state.storedCountryForms?.[country] &&
        Object.keys(state.storedCountryForms[country]).length > 0 && {
          storedCountryForms: {
            ...state.storedCountryForms,
            [country]: {
              ...state.storedCountryForms[country],
              metaConfig: {
                ...state.storedCountryForms[country].metaConfig,
                metaCampaignId,
                metaPredictionId,
              },
            },
          },
        }),
    })),

  setAlreadySavedPlanId: (id?: string) =>
    set(() => ({ alreadySavedPlanId: id || null })),

  setGeneralInfoState: (state) =>
    set((prev) => ({
      generalInfoState: {
        ...prev.generalInfoState,
        ...state,
      },
    })),
  resetGeneralInfoState: () =>
    set({
      generalInfoState: {
        campaignName: '',
        campaignCode: '',
        division: '',
        brand: '',
        category: '',
      },
    }),

  // Saved General Info State
  savedGeneralInfoState: null,
  saveGeneralInfoState: () =>
    set((state) => ({
      savedGeneralInfoState: { ...state.generalInfoState },
    })),
  resetSavedGeneralInfoState: () =>
    set((state) => ({
      savedGeneralInfoState: null,
    })),
  revertGeneralInfoState: () =>
    set((state) => ({
      generalInfoState: state.savedGeneralInfoState
        ? { ...state.savedGeneralInfoState }
        : {
            campaignName: '',
            campaignCode: '',
            division: '',
            brand: '',
            category: '',
          },
    })),

  // Country Management
  countryForms: {},
  addCountry: (country) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          targetGroup: '',
          campaignScenario: '',
          segments: [],
          startDate: '',
          campaignLength: '',
          isTVIncluded: null,
          isTVMandatory: false,
          isCountrySetupConfirmed: false,
          youTubeConfig: undefined,
          metaConfig: undefined,
          tiktokConfig: undefined,
          calculatedBudget: [],
          savedCalculatedBudget: [],
          ddx: null,
          oneReach: undefined,
          offline: {
            savedTVConfig: {
              isAvailable: false,
              weeklyWeight: {
                min: 40,
                max: 120,
                current: 40,
              },
              startDate: undefined,
              endDate: undefined,
              minCampaignLength: undefined,
              maxCampaignLength: undefined,
              targetGroup: undefined,
              copyForm: [
                {
                  copyLength: undefined,
                  additionalAdvertisers: '0',
                  advertisers: [],
                  id: '10s_0_0',
                },
              ],
              calculatedBudget: undefined,
              savedCalculatedBudget: undefined,
            },
            tv: {
              isAvailable: false,
              weeklyWeight: {
                min: 40,
                max: 120,
                current: 40,
              },
              startDate: undefined,
              endDate: undefined,
              minCampaignLength: undefined,
              maxCampaignLength: undefined,
              targetGroup: undefined,
              maxActWeeks: undefined,
              maxWeeklyGrp: undefined,
              minActWeeks: undefined,
              minWeeklyGrp: undefined,
              copyForm: [
                {
                  copyLength: undefined,
                  additionalAdvertisers: '0',
                  advertisers: [],
                  id: '10s_0_0',
                },
              ],
              calculatedBudget: undefined,
              savedCalculatedBudget: undefined,
            },
            ooh: {
              isAvailable: false,
              isRequired: false,
              calculatedBudget: undefined,
              savedCalculatedBudget: undefined,
            },
          },
        },
      },
    })),
  updateCountryForm: (country, data) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          ...data,
        },
      },
    })),
  updateYouTubeConfig: (country, config) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          youTubeConfig: {
            ...state.countryForms[country].youTubeConfig,
            ...config,
          },
        },
      },
    })),
  updateMetaConfig: (country, config) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          metaConfig: {
            ...state.countryForms[country].metaConfig,
            ...config,
          },
        },
      },
    })),
  updateTikTokConfig: (country, config) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          tiktokConfig: {
            ...state.countryForms[country].tiktokConfig,
            ...config,
          },
        },
      },
    })),
  updateCalculatedBudget: (country, budget) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          calculatedBudget: budget,
        },
      },
    })),
  updateCalculatedOOHBudget: (country: string, budget: OOHBudget) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline;
      const currentOOH = currentOffline?.ooh;
      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              ooh: {
                isAvailable: currentOOH?.isAvailable ?? false,
                ...currentOOH?.calculatedBudget,
                calculatedBudget: budget,
              },
            },
          },
        },
      };
    }),

  setCountryOneReach: (country: string, oneReach?: number) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            oneReach,
          },
        },
      };
    }),

  updateOOHAvailable: (country: string, isAvailable: boolean) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline;
      const currentOOH = currentOffline?.ooh;
      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              ooh: {
                ...currentOOH,
                isAvailable,
              },
            },
          },
        },
      };
    }),

  updateTVAvailable: (country: string, isAvailable: boolean) =>
    set((state) => {
      const currentForm =
        state.countryForms[country] ?? ({} as CountryFormState);
      const currentOffline = currentForm.offline ?? {};
      const currentTV = currentOffline.tv ?? { copyForm: [] };

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                isAvailable,
              },
            },
          },
        },
      };
    }),

  updateCalculatedTVBudget: (
    country: string,
    budget: Partial<CalculatedTVBudget>
  ) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline;
      const currentTV = currentOffline?.tv;

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                calculatedBudget: {
                  ...currentTV?.calculatedBudget,
                  ...budget,
                },
                copyForm: currentTV?.copyForm ?? [],
              },
            },
          },
        },
      };
    }),

  resetOfflineTables: (country: string) =>
    set((state) => {
      if (!state.countryForms[country]) return state;

      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline || {};
      const currentTV = currentOffline.tv || {
        copyForm: [
          {
            copyLength: undefined,
            additionalAdvertisers: '0',
            advertisers: [],
            id: '10s_0_0',
          },
        ],
      };

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                grpTable: undefined,
                copyForm: [
                  {
                    copyLength: undefined,
                    additionalAdvertisers: '0',
                    advertisers: [],
                    id: '10s_0_0',
                  },
                ],
                calculatedBudget: undefined,
                savedCalculatedBudget: undefined,
                minCampaignLength: undefined,
                maxCampaignLength: undefined,
              },
              ooh: {
                isAvailable: false,
                isRequired: false,
                calculatedBudget: undefined,
                savedCalculatedBudget: undefined,
              },
            },
          },
        },
      };
    }),

  resetCalculatedBudget: (country: string) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          calculatedBudget: undefined,
        },
      },
    })),
  updateSavedCalculatedBudget: (country, budget) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          savedCalculatedBudget: budget,
        },
      },
    })),

  syncMetaState: (country, metaPredictionId, budget) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          savedCalculatedBudget: budget,
        },
      },
      savedCountryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          metaConfig: {
            ...state.countryForms[country].metaConfig,
            metaPredictionId,
          },
        },
      },
    })),
  resetSavedCalculatedBudget: (country: string) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          savedCalculatedBudget: undefined,
        },
      },
    })),

  updateOfflineBudget: (country: string) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline || {};
      const currentTV: TVConfig = currentOffline.tv || {
        copyForm: [],
        offlineBudget: [],
      };

      const currentOOH: OOHConfig = currentOffline.ooh || {
        isAvailable: false,
        calculatedBudget: {
          isRequired: false,
          data: [],
        },
        savedCalculatedBudget: {
          isRequired: false,
          data: [],
        },
      };

      const tvBudget = currentTV?.calculatedBudget;
      const oohBudget = currentOOH?.calculatedBudget;
      const techCostBudget = {
        data: [
          {
            budget: tvBudget?.techCost,
            format: undefined,
            isRequired: undefined,
            variant: undefined,
          },
        ],
      };

      const offlineBudget: OfflineBudgetEntry[] = [];

      if (tvBudget && tvBudget.copies) {
        offlineBudget.push({
          id: 1,
          touchpoint: 'tv',
          tvReachOne: tvBudget.reach1Plus,
          tvReachThree: tvBudget.reach3Plus,
          grp: tvBudget.copies.reduce(
            (sum: number, copy: ResponseCopy) => sum + copy.totalGrp,
            0
          ),
          weeks: tvBudget.copies[0]?.weeks ?? 0,
          budget: tvBudget.copies.reduce(
            (acc, copy: ResponseCopy) => ({
              currency: copy.campaignBudget.currency,
              value: acc.value + copy.campaignBudget.value,
            }),
            { currency: '', value: 0 }
          ),

          children: tvBudget.copies.map(
            (copy: ResponseCopy, index: number) => ({
              id: index,
              copy: {
                name: copy.name,
                id: copy.id,
              },
              grp: copy.totalGrp,
              split: copy.split,
              weeks: copy.weeks,
              budget: {
                value: copy.campaignBudget.value,
                currency: copy.campaignBudget.currency,
              },
            })
          ),
        });
      }

      if (oohBudget) {
        offlineBudget.push({
          id: 2,
          touchpoint: 'outdoor',
          budget: oohBudget.data
            .filter((entry) => entry.isIncludedInCalculations)
            .reduce(
              (acc, entry) => ({
                currency: entry.budget.currency,
                value: acc.value + entry.budget.value,
              }),
              { currency: '', value: 0 }
            ),
          children: oohBudget.data.map((entry, index) => ({
            id: index,
            type: entry.format,
            option: entry.variant,
            budget: entry.budget,
            isIncludedInCalculations:
              entry.isIncludedInCalculations ?? entry.isRequired,
          })),
        });
      }

      if (techCostBudget) {
        offlineBudget.push({
          id: 3,
          touchpoint: 'tech cost',
          budget: techCostBudget.data[0].budget || {
            value: 0,
            currency: 'PLN',
          },
          children: techCostBudget.data.map((entry, index) => ({
            id: index,
            type: entry.format,
            option: entry.variant,
            budget: entry.budget ?? {
              currency: 'PLN',
              value: 0,
            },
          })),
        });
      }

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                offlineBudget,
              },
            },
          },
        },
      };
    }),

  resetOfflineBudget: (country: string) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline || {};
      const currentTV: TVConfig = currentOffline.tv || {
        copyForm: [],
        offlineBudget: [],
      };

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                copyForm: currentTV.copyForm ?? [],
                offlineBudget: [],
              },
            },
          },
        },
      };
    }),

  removeCountry: (country) =>
    set((state) => {
      const updatedForms = { ...state.countryForms };
      delete updatedForms[country];
      return { countryForms: updatedForms };
    }),
  resetCountryForms: () =>
    set({
      countryForms: {},
    }),
  isCountryFormValid: (country) => {
    const form = get().countryForms[country];
    if (!form) return false;
    return Object.values(form).every((value) => value !== null && value !== '');
  },

  // Saved Country States
  savedCountryForms: {},
  storedCountryForms: {},
  saveCountryFormState: (country) => {
    const form = get().countryForms[country];
    if (form) {
      set((state) => ({
        savedCountryForms: {
          ...state.savedCountryForms,
          [country]: {
            targetGroup: form.targetGroup,
            campaignScenario: form.campaignScenario,
            segments: form.segments,
            startDate: form.startDate,
            campaignLength: form.campaignLength,
            isTVIncluded: form.isTVIncluded,
            isTVMandatory: form.isTVMandatory,
            offline: form.offline,
            calculatedBudget: form.calculatedBudget,
            youTubeConfig: form.youTubeConfig,
            metaConfig: form.metaConfig,
            tiktokConfig: form.tiktokConfig,
          },
        },
      }));
    }
  },
  storeCountryFormState: (country) => {
    const form = get().countryForms[country];
    if (form) {
      set((state) => ({
        storedCountryForms: {
          ...state.storedCountryForms,
          [country]: {
            targetGroup: form.targetGroup,
            availableTouchpoints: form.availableTouchpoints,
            campaignScenario: form.campaignScenario,
            segments: form.segments,
            startDate: form.startDate,
            campaignLength: form.campaignLength,
            isTVIncluded: form.isTVIncluded,
            isTVMandatory: form.isTVMandatory,
            offline: form.offline,
            calculatedBudget: form.calculatedBudget,
            youTubeConfig: form.youTubeConfig,
            metaConfig: form.metaConfig,
            tiktokConfig: form.tiktokConfig,
            oneReach: form.oneReach,
            savedCalculatedBudget: form.savedCalculatedBudget,
            isCountrySetupConfirmed: form.isCountrySetupConfirmed,
            ddx: form.ddx,
          },
        },
      }));
    }
  },
  saveDigitalCountryFormState: (country: string) => {
    const form = get().countryForms[country];
    if (form) {
      set((state) => ({
        savedCountryForms: {
          ...state.savedCountryForms,
          [country]: {
            ...state.savedCountryForms[country],
            targetGroup: form.targetGroup,
            campaignScenario: form.campaignScenario,
            segments: form.segments,
            startDate: form.startDate,
            campaignLength: form.campaignLength,
            isTVIncluded: form.isTVIncluded,
            isTVMandatory: form.isTVMandatory,
            availableTouchpoints: form.availableTouchpoints,
            isCountrySetupConfirmed: form.isCountrySetupConfirmed,
            calculatedBudget: form.calculatedBudget,
            youTubeConfig: form.youTubeConfig,
            metaConfig: form.metaConfig,
            tiktokConfig: form.tiktokConfig,
            oneReach: form.oneReach,
          },
        },
      }));
    }
  },
  storeDigitalCountryFormState: (country: string) => {
    const form = get().countryForms[country];
    if (form) {
      set((state) => ({
        storedCountryForms: {
          ...state.storedCountryForms,
          [country]: {
            ...state.storedCountryForms[country],
            targetGroup: form.targetGroup,
            campaignScenario: form.campaignScenario,
            segments: form.segments,
            startDate: form.startDate,
            campaignLength: form.campaignLength,
            isTVIncluded: form.isTVIncluded,
            isTVMandatory: form.isTVMandatory,
            availableTouchpoints: form.availableTouchpoints,
            isCountrySetupConfirmed: form.isCountrySetupConfirmed,
            calculatedBudget: form.calculatedBudget,
            savedCalculatedBudget: form.savedCalculatedBudget,
            youTubeConfig: form.youTubeConfig,
            metaConfig: form.metaConfig,
            tiktokConfig: form.tiktokConfig,
            oneReach: form.oneReach,
            ddx: form.ddx,
          },
        },
      }));
    }
  },
  saveOfflineCountryFormState: (country: string) => {
    const form = get().countryForms[country];
    if (form) {
      set((state) => ({
        savedCountryForms: {
          ...state.savedCountryForms,
          [country]: {
            ...state.savedCountryForms[country],
            targetGroup: form.targetGroup,
            campaignScenario: form.campaignScenario,
            segments: form.segments,
            startDate: form.startDate,
            campaignLength: form.campaignLength,
            isTVIncluded: form.isTVIncluded,
            isTVMandatory: form.isTVMandatory,
            offline: form.offline,
          },
        },
      }));
    }
  },
  storeOfflineCountryFormState: (country: string) => {
    const form = get().countryForms[country];
    if (form) {
      set((state) => ({
        storedCountryForms: {
          ...state.storedCountryForms,
          [country]: {
            ...state.storedCountryForms[country],
            targetGroup: form.targetGroup,
            campaignScenario: form.campaignScenario,
            segments: form.segments,
            startDate: form.startDate,
            campaignLength: form.campaignLength,
            isTVIncluded: form.isTVIncluded,
            isTVMandatory: form.isTVMandatory,
            offline: form.offline,
            oneReach: form.oneReach,
            ddx: form.ddx,
          },
        },
      }));
    }
  },
  revertCountryFormState: (country) => {
    const savedForm = get().savedCountryForms[country];
    if (savedForm) {
      set((state) => ({
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...state.countryForms[country],
            ...savedForm,
          },
        },
      }));
    }
  },
  resetAllSavedCountryForms: () =>
    set(() => ({
      savedCountryForms: {},
    })),
  resetAllStoredCountryForms: () =>
    set(() => ({
      storedCountryForms: {},
    })),
  resetStoredCountryForm: (country: string) => {
    set((state) => {
      const updatedForms = { ...state.storedCountryForms };
      delete updatedForms[country];
      return { storedCountryForms: updatedForms };
    });
  },

  resetSavedCountryForm: (country: string) => {
    set((state) => {
      const updatedForms = { ...state.savedCountryForms };
      delete updatedForms[country];
      return { savedCountryForms: updatedForms };
    });
  },

  // Reset Configuration Only
  resetCountryFormConfig: (country) => {
    set((state) => {
      const currentForm = state.countryForms[country];
      if (!currentForm) {
        return state;
      }
      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            youTubeConfig: undefined,
            metaConfig: undefined,
            tiktokConfig: undefined,
          },
        },
      };
    });
  },

  updateTVConfig: (
    country: string,
    tvConfig: Partial<TVConfig>,
    copyIndex?: number
  ) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline || {};
      const currentTV: TVConfig = currentOffline.tv || { copyForm: [] };

      let updatedCopyForm = [...currentTV.copyForm];

      if (copyIndex !== undefined && tvConfig.copyForm) {
        while (updatedCopyForm.length <= copyIndex) {
          updatedCopyForm.push({});
        }

        updatedCopyForm[copyIndex] = {
          ...updatedCopyForm[copyIndex],
          ...(tvConfig.copyForm[0] || {}),
        };
      } else if (tvConfig.copyForm) {
        updatedCopyForm = tvConfig.copyForm;
      }

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                ...tvConfig,
                copyForm: updatedCopyForm,
              },
            },
          },
        },
      };
    }),

  saveTVConfig: (country: string) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline || {};
      const currentTV = currentOffline.tv;

      if (!currentTV) return state;

      const { grpTable, ...tvConfigWithoutGrpTable } = currentTV;

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
              },
              savedTVConfig: tvConfigWithoutGrpTable,
            },
          },
        },
      };
    }),

  resetTVConfig: (country: string) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          offline: {
            ...state.countryForms[country].offline,
            savedTVConfig: {},
            tv: {
              isAvailable: false,
              weeklyWeight: {
                min: 40,
                max: 120,
                current: 40,
              },
              startDate: undefined,
              endDate: undefined,
              minCampaignLength: undefined,
              maxCampaignLength: undefined,
              targetGroup: undefined,
              copyForm: [
                {
                  copyLength: undefined,
                  additionalAdvertisers: '0',
                  advertisers: [],
                  id: '10s_0_0',
                },
              ],
              calculatedBudget: undefined,
              savedCalculatedBudget: undefined,
            },
          },
        },
      },
    })),

  resetOOHConfig: (country: string) =>
    set((state) => ({
      countryForms: {
        ...state.countryForms,
        [country]: {
          ...state.countryForms[country],
          offline: {
            ...state.countryForms[country].offline,
            ooh: {
              ...state.countryForms[country].offline?.ooh,
              isAvailable: false,
              calculatedBudget: undefined,
              savedCalculatedBudget: undefined,
            },
          },
        },
      },
    })),

  updateCopyFormField: (
    country: string,
    copyIndex: number,
    field: keyof CopyFormEntry,
    value: any
  ) =>
    set((state) => {
      if (!state.countryForms[country]) return state;

      const existingCopyForm =
        state.countryForms[country]?.offline?.tv?.copyForm || [];

      const updatedCopyForm = [...existingCopyForm];
      if (!updatedCopyForm[copyIndex]) {
        updatedCopyForm[copyIndex] = {};
      }

      updatedCopyForm[copyIndex] = {
        ...updatedCopyForm[copyIndex],
        [field]: value.length ? value : [], // Ensures unselecting resets to an empty array
      };

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...state.countryForms[country],
            offline: {
              ...state.countryForms[country].offline,
              tv: {
                ...state.countryForms[country].offline?.tv,
                copyForm: updatedCopyForm,
              },
            },
          },
        },
      };
    }),

  addCopyFormEntry: (country: string, index: number) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline || {};
      const currentTV = currentOffline.tv || { copyForm: [] };
      const updatedCopyForm = [
        ...currentTV.copyForm,
        {
          copyLength: undefined,
          additionalAdvertrisers: '0',
          advertisers: [],
          id: `10s_0_${index}`,
        },
      ];

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                copyForm: updatedCopyForm,
              },
            },
          },
        },
      };
    }),

  removeCopyFormEntry: (country: string, copyIndex: number) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline || {};
      const currentTV = currentOffline.tv || { copyForm: [] };
      const updatedCopyForm = [...currentTV.copyForm];

      if (copyIndex >= 0 && copyIndex < updatedCopyForm.length) {
        updatedCopyForm.splice(copyIndex, 1);
      }

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                copyForm: updatedCopyForm,
              },
            },
          },
        },
      };
    }),

  resetCopyFormEntry: (country: string, copyIndex: number) =>
    set((state) => {
      const currentForm = state.countryForms[country] || {};
      const currentOffline = currentForm.offline || {};
      const currentTV = currentOffline.tv || { copyForm: [] };
      const updatedCopyForm = [...currentTV.copyForm];

      if (updatedCopyForm[copyIndex]) {
        updatedCopyForm[copyIndex] = {
          copyLength: undefined,
          additionalAdvertisers: '0',
          advertisers: [],
          id: '10s_0_0',
        };
      }

      return {
        countryForms: {
          ...state.countryForms,
          [country]: {
            ...currentForm,
            offline: {
              ...currentOffline,
              tv: {
                ...currentTV,
                copyForm: updatedCopyForm,
              },
            },
          },
        },
      };
    }),

  // Validation Management
  validationErrors: {},
  validateCountryForm: (country, data) => {
    try {
      countryFormSchema.validateSync(data, { abortEarly: false });
      set((state) => ({
        validationErrors: { ...state.validationErrors, [country]: {} },
      }));
    } catch (err) {
      const validationErrors: Record<string, string> = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((e) => {
          if (e.path) validationErrors[e.path] = e.message;
        });
      }
      set((state) => ({
        validationErrors: {
          ...state.validationErrors,
          [country]: validationErrors,
        },
      }));
    }
  },

  selectedCountry: null,
  selectedCountryISOCode: null,
  setSelectedCountry: (country) => set({ selectedCountry: country }),
  setSelectedCountryISOCode: (country) => {
    if (!country) {
      set({ selectedCountryISOCode: null });
      return;
    }
    const isoCode =
      (Countries as Record<string, string>)[country.toUpperCase()] || null;
    set({ selectedCountryISOCode: isoCode });
  },
}));

export default usePlanStore;
